import { FunctionComponent, useEffect, useState } from 'react'

import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { MapJob } from './map-types'
import { Link as IconLink } from '../../icons'
import { Col, Row, Typography } from 'antd'
import { MAP_JOB_COLORS } from './map-constants'
import { useTranslation } from 'react-i18next'

const { Link } = Typography

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

/**
 * Properties type for JobResume
 */
export type JobResumeProps = {
  job: MapJob
  interval?: number
  onJobClick?: () => void
  icon?: React.ReactElement
  timezone: string
}

/**
 * Job resume for job map.
 *
 * @param properties  Job resume properties.
 */
export const JobResume: FunctionComponent<JobResumeProps> = ({
  job,
  interval = 60000,
  onJobClick = () => {},
  icon,
  timezone
}) => {
  const { t } = useTranslation()
  const [slotToText, setSlotToText] = useState<string | undefined>('')
  let timer: NodeJS.Timeout | null = null
  const { jobNumber, partialStatus } = job
  const style = job.partialStatus
    ? {
        background: MAP_JOB_COLORS[job.partialStatus]?.bg,
        color: MAP_JOB_COLORS[job.partialStatus]?.text
      }
    : undefined

  const startTimer = () => {
    clearTimer()
    timer = setInterval(() => {
      updateTime()
    }, interval)
  }

  const clearTimer = () => {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  const updateTime = () => {
    const { slotTo: dateSlotTo } = job
    if (dateSlotTo) {
      setSlotToText(dayjs(dateSlotTo).tz(timezone).fromNow())
    } else {
      setSlotToText('')
    }
  }

  useEffect(() => {
    const { slotTo: dateSlotTo } = job as MapJob
    if (dateSlotTo) {
      updateTime()
      startTimer()
    }

    return () => {
      clearTimer()
    }
  }, [])

  return (
    <Row className='gungnir-jm-job-resume' gutter={8}>
      {icon && (
        <Col className='gungnir-jm-jr-col-icon' span={6}>
          <div className='gungnir-jm-jr-icon' style={style}>
            {icon}
          </div>
        </Col>
      )}
      <Col span={icon ? 18 : 24}>
        <Link
          className='gungnir-title'
          onClick={() => {
            onJobClick && onJobClick()
          }}
        >
          {jobNumber} <IconLink />
        </Link>
        <div className='gungnir-jm-gray'>{t(partialStatus)}</div>
        <time className='gungnir-jm-slot'>{slotToText}</time>
      </Col>
    </Row>
  )
}
